// Taking the waiting off an incident's clock. Only the resolution target is measured on it, and nothing
// in this slice can create a pause.

/** One stretch, as the API records it. `endedAt` is null while the wait is still on. */
export interface IncidentPause {
  id: string;
  startedAt: string;
  endedAt: string | null;
  reason: string | null;
}

/** A stretch of clock in milliseconds, with both ends known. */
export interface PauseSpan {
  fromMs: number;
  toMs: number;
}

/** The pauses as spans on the clock, overlaps merged, in the order they happened. */
export const pauseSpans = (pauses: IncidentPause[], nowMs = Date.now()): PauseSpan[] => {
  const spans = pauses
    .map(pause => ({
      fromMs: Date.parse(pause.startedAt),
      toMs: pause.endedAt ? Date.parse(pause.endedAt) : nowMs,
    }))
    .filter(span => Number.isFinite(span.fromMs) && span.toMs > span.fromMs)
    .sort((a, b) => a.fromMs - b.fromMs);

  return spans.reduce<PauseSpan[]>((merged, span) => {
    const last = merged[merged.length - 1];
    if (last && span.fromMs <= last.toMs) {
      return [...merged.slice(0, -1), { ...last, toMs: Math.max(last.toMs, span.toMs) }];
    }
    return [...merged, { ...span }];
  }, []);
};

/** How much of the stretch between two moments was spent waiting. */
export const pausedMsBetween = (spans: PauseSpan[], fromMs: number, toMs: number): number =>
  spans.reduce((total, span) => total + Math.max(0, Math.min(span.toMs, toMs) - Math.max(span.fromMs, fromMs)), 0);

/** When a promise of this length actually runs out, given what was waited on. */
export const deadlineAfterPauses = (startMs: number, targetMs: number, spans: PauseSpan[]): number =>
  spans.reduce((deadline, span) => {
    if (span.fromMs >= deadline) return deadline;
    // A pause wholly before the incident came in waited on nothing, so it cannot shorten the promise.
    return deadline + Math.max(0, span.toMs - Math.max(span.fromMs, startMs));
  }, targetMs);

/** The clock an incident is judged on: the time since it came in, less what was waited on. */
export const workingMsBetween = (spans: PauseSpan[], fromMs: number, toMs: number): number =>
  Math.max(0, toMs - fromMs - pausedMsBetween(spans, fromMs, toMs));
